/**
 * Contest self-evaluation helpers — SEPARATE from Training / Ranking self-evaluation.
 * Stores drafts and saved contest performance values against C/B/A grade bands only.
 */

import type { ContestCatalogEntry, ContestGradeCode, ContestSectionKey } from '../types/contestCatalog';
import {
  assessGradeFromValue,
  getBenchmarkUnit,
  getContestsGroupedByCategory,
  getSectionContentForAge,
  getSportContestCatalog,
  type ContestCategoryGroup,
} from './contestCatalog';
import { toSportLabel, toSportSlug } from './sports';

export interface ContestSelfEvalEntry {
  contestId: string;
  value: string;
  /** YYYY-MM-DD — date the player recorded this result */
  recordedDate: string;
  selfNotes: string;
  coachNotes: string;
  /** Selected common mistakes (self-eval Error column only). */
  errors: string[];
  grade: ContestGradeCode | null;
  assessedAt?: string;
  /** Set when this skill was saved; cleared if the row is edited again. */
  savedAt?: string;
  /** @deprecated use selfNotes */
  notes?: string;
}

export type ContestSelfEvalConsentRole = 'self' | 'parent';

export interface ContestSelfEvalSkillConsent {
  followedGuidelines: boolean;
  consented: boolean;
}

export interface ContestSelfEvalConsentRecord {
  role: ContestSelfEvalConsentRole;
  attested: boolean;
  attestedAt: string;
  action: 'save' | 'submit' | 'video';
  skills: Record<string, ContestSelfEvalSkillConsent>;
}

export interface ContestSelfEvalDraft {
  version: 1;
  /** Discriminator — must never mix with ranking/training eval payloads */
  kind: 'contest_self_evaluation';
  sport: string;
  ageCategory: string;
  contestTypeId: 'foundation';
  entries: Record<string, ContestSelfEvalEntry>;
  lastConsent?: ContestSelfEvalConsentRecord;
  status?: 'draft' | 'saved' | 'submitted';
  submittedAt?: string;
  updatedAt: string;
}

export const MAX_SELF_EVAL_SKILLS_PER_DATE = 2;

const DRAFT_PREFIX = 'encycgames.contestSelfEval.draft.v1';
const SAVED_PREFIX = 'encycgames.contestSelfEval.saved.v1';
const PENDING_KEY = 'encycgames.contestSelfEval.pending.v1';

function normalizeSportKey(sport: string): string {
  return toSportLabel(sport);
}

function draftKey(sport: string, ageCategory: string, playerId?: string | null): string {
  const base = `${DRAFT_PREFIX}:${normalizeSportKey(sport)}:${ageCategory}`;
  return playerId ? `${base}:${playerId}` : base;
}

function savedKey(ownerId: string, sport: string, ageCategory: string): string {
  return `${SAVED_PREFIX}:${ownerId}:${normalizeSportKey(sport)}:${ageCategory}`;
}

function readBrowserItem(key: string): string | null {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeBrowserItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function draftHasFilledValues(draft: ContestSelfEvalDraft | null | undefined): boolean {
  if (!draft) return false;
  return Object.values(draft.entries || {}).some((entry) => String(entry?.value || '').trim() !== '');
}

function normalizeContestSelfEvalEntry(
  contestId: string,
  raw: Partial<ContestSelfEvalEntry> & { notes?: string }
): ContestSelfEvalEntry {
  const errors = Array.isArray(raw.errors)
    ? [...new Set(raw.errors.map((item) => String(item || '').trim()).filter(Boolean))]
    : [];
  return {
    contestId,
    value: raw.value ?? '',
    recordedDate: raw.recordedDate ?? '',
    selfNotes: raw.selfNotes ?? raw.notes ?? '',
    coachNotes: raw.coachNotes ?? '',
    errors,
    grade: raw.grade ?? null,
    assessedAt: raw.assessedAt,
    savedAt: raw.savedAt,
  };
}

function normalizeContestSelfEvalEntries(
  entries: Record<string, Partial<ContestSelfEvalEntry> & { notes?: string }>
): Record<string, ContestSelfEvalEntry> {
  return Object.fromEntries(
    Object.entries(entries).map(([contestId, entry]) => [
      contestId,
      normalizeContestSelfEvalEntry(contestId, entry),
    ])
  );
}

export function createEmptyContestSelfEvalDraft(
  sport: string,
  ageCategory: string
): ContestSelfEvalDraft {
  return {
    version: 1,
    kind: 'contest_self_evaluation',
    sport: normalizeSportKey(sport),
    ageCategory,
    contestTypeId: 'foundation',
    entries: {},
    status: 'draft',
    updatedAt: new Date().toISOString(),
  };
}

export function loadContestSelfEvalDraft(
  sport: string,
  ageCategory: string,
  playerId?: string | null
): ContestSelfEvalDraft {
  try {
    const raw =
      readBrowserItem(draftKey(sport, ageCategory, playerId)) ||
      (!playerId ? readBrowserItem(`${DRAFT_PREFIX}:${sport}:${ageCategory}`) : null);
    if (!raw) return createEmptyContestSelfEvalDraft(sport, ageCategory);
    const parsed = JSON.parse(raw) as ContestSelfEvalDraft;
    if (parsed?.kind !== 'contest_self_evaluation') {
      return createEmptyContestSelfEvalDraft(sport, ageCategory);
    }
    return {
      ...createEmptyContestSelfEvalDraft(sport, ageCategory),
      ...parsed,
      sport: normalizeSportKey(sport),
      ageCategory,
      entries: normalizeContestSelfEvalEntries(parsed.entries || {}),
    };
  } catch {
    return createEmptyContestSelfEvalDraft(sport, ageCategory);
  }
}

export function saveContestSelfEvalDraft(draft: ContestSelfEvalDraft, playerId?: string | null): void {
  const next = {
    ...draft,
    sport: normalizeSportKey(draft.sport),
    kind: 'contest_self_evaluation' as const,
    updatedAt: new Date().toISOString(),
  };
  writeBrowserItem(draftKey(next.sport, next.ageCategory, playerId), JSON.stringify(next));
}

export function loadSavedContestSelfEval(
  userId: string,
  sport: string,
  ageCategory: string
): ContestSelfEvalDraft | null {
  try {
    const raw =
      readBrowserItem(savedKey(userId, sport, ageCategory)) ||
      localStorage.getItem(`${SAVED_PREFIX}:${userId}:${sport}:${ageCategory}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ContestSelfEvalDraft;
    if (parsed?.kind !== 'contest_self_evaluation') return null;
    return {
      ...parsed,
      entries: normalizeContestSelfEvalEntries(parsed.entries || {}),
    };
  } catch {
    return null;
  }
}

export function persistContestSelfEvalForUser(
  userId: string,
  draft: ContestSelfEvalDraft
): void {
  const next = {
    ...draft,
    sport: normalizeSportKey(draft.sport),
    kind: 'contest_self_evaluation' as const,
    updatedAt: new Date().toISOString(),
  };
  writeBrowserItem(savedKey(userId, next.sport, next.ageCategory), JSON.stringify(next));
  saveContestSelfEvalDraft(next, userId);
}

export function clearContestSelfEvalDraft(sport: string, ageCategory: string, playerId?: string | null): void {
  const keys = [
    draftKey(sport, ageCategory, playerId),
    draftKey(sport, ageCategory),
    `${DRAFT_PREFIX}:${sport}:${ageCategory}`,
  ];
  keys.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
}

export function rememberSelfEvalResume(payload: {
  sport: string;
  ageCategory: string;
  action: 'save' | 'submit';
}): void {
  try {
    localStorage.setItem(
      PENDING_KEY,
      JSON.stringify({
        sport: normalizeSportKey(payload.sport),
        ageCategory: payload.ageCategory,
        action: payload.action,
      }),
    );
  } catch {
    /* ignore */
  }
}

export function consumeSelfEvalResumeIfMatch(
  sport: string,
  ageCategory: string,
): 'save' | 'submit' | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { sport?: string; ageCategory?: string; action?: string };
    if (
      normalizeSportKey(String(parsed.sport || '')) !== normalizeSportKey(sport) ||
      String(parsed.ageCategory || '') !== ageCategory
    ) {
      return null;
    }
    localStorage.removeItem(PENDING_KEY);
    return parsed.action === 'submit' ? 'submit' : 'save';
  } catch {
    return null;
  }
}

/**
 * After login: a saved/DB scorecard with values wins over the guest draft.
 * If the account has no saved values yet, keep the pre-login draft.
 */
function mergeSelfEvalEntries(
  ...sources: Array<Record<string, ContestSelfEvalEntry> | undefined | null>
): Record<string, ContestSelfEvalEntry> {
  const merged: Record<string, ContestSelfEvalEntry> = {};
  sources.forEach((entries) => {
    Object.entries(entries || {}).forEach(([id, raw]) => {
      const key = String(raw?.contestId || id);
      if (!key) return;
      const incoming = normalizeContestSelfEvalEntry(key, raw || {});
      const current = merged[key];
      if (!current) {
        merged[key] = incoming;
        return;
      }
      merged[key] = {
        ...current,
        ...incoming,
        contestId: key,
        value: String(incoming.value || '').trim() ? incoming.value : current.value,
        recordedDate: String(incoming.recordedDate || '').trim()
          ? incoming.recordedDate
          : current.recordedDate,
        selfNotes: String(incoming.selfNotes || '').trim() ? incoming.selfNotes : current.selfNotes,
        coachNotes: String(incoming.coachNotes || '').trim() ? incoming.coachNotes : current.coachNotes,
        errors: incoming.errors?.length ? incoming.errors : current.errors,
        grade: incoming.grade ?? current.grade,
        savedAt: incoming.savedAt || current.savedAt,
      };
    });
  });
  return merged;
}

export function pickSelfEvalHydration(options: {
  remote?: ContestSelfEvalDraft | null;
  saved?: ContestSelfEvalDraft | null;
  guest?: ContestSelfEvalDraft | null;
  sport: string;
  ageCategory: string;
}): ContestSelfEvalDraft {
  const empty = createEmptyContestSelfEvalDraft(options.sport, options.ageCategory);
  if (isSelfEvalLockedThisQuarter(options.remote?.status, options.remote?.submittedAt)) {
    return options.remote as ContestSelfEvalDraft;
  }
  const base = options.remote || options.saved || options.guest || empty;
  return {
    ...empty,
    ...base,
    sport: options.sport,
    ageCategory: options.ageCategory,
    kind: 'contest_self_evaluation',
    entries: mergeSelfEvalEntries(
      options.guest?.entries,
      options.saved?.entries,
      options.remote?.entries,
    ),
    status: options.remote?.status || options.saved?.status || options.guest?.status || 'draft',
    submittedAt: options.remote?.submittedAt || options.saved?.submittedAt || options.guest?.submittedAt,
  };
}

export { isSafeAppReturnUrl } from './safeReturnUrl';

export function contestSelfEvalPath(sportSlug: string): string {
  return `/contests/${sportSlug}/self-evaluation`;
}

export function getAllFoundationContests(sport: string): ContestCategoryGroup[] {
  const catalog = getSportContestCatalog(sport);
  if (!catalog) return [];
  return getContestsGroupedByCategory(catalog, 'foundation');
}

export function assessEntryValue(
  contest: ContestCatalogEntry,
  ageCategory: string,
  valueText: string,
  genderKey?: import('../types/contestCatalog').ContestGenderKey | null,
): ContestGradeCode | null {
  const n = Number.parseFloat(valueText.trim());
  if (!Number.isFinite(n)) return null;
  return assessGradeFromValue(contest, ageCategory, n, genderKey).grade;
}

export interface SelfEvalGradeChange {
  contestId: string;
  title: string;
  from: ContestGradeCode | null;
  to: ContestGradeCode | null;
}

/** Re-map stored scores against the current catalog. Values stay; grades follow live targets. */
export function reassessSelfEvalDraftGrades(
  draft: ContestSelfEvalDraft,
  contests: ContestCatalogEntry[],
  ageCategory: string,
  genderKey?: import('../types/contestCatalog').ContestGenderKey | null,
): { draft: ContestSelfEvalDraft; changes: SelfEvalGradeChange[] } {
  if (!draft?.entries || !contests?.length) return { draft, changes: [] };
  const byId = new Map(contests.map((contest) => [String(contest.id), contest]));
  const changes: SelfEvalGradeChange[] = [];
  const entries: Record<string, ContestSelfEvalEntry> = { ...draft.entries };
  Object.entries(entries).forEach(([id, entry]) => {
    const value = String(entry?.value || '').trim();
    if (!value) return;
    const contest = byId.get(id) || (entry?.contestId ? byId.get(String(entry.contestId)) : undefined);
    if (!contest) return;
    const nextGrade = assessEntryValue(contest, ageCategory, value, genderKey);
    const prevGrade = entry.grade ?? null;
    if (String(prevGrade || '') === String(nextGrade || '')) return;
    changes.push({
      contestId: id,
      title: contest.title || id,
      from: prevGrade,
      to: nextGrade,
    });
    entries[id] = {
      ...entry,
      contestId: entry.contestId || id,
      grade: nextGrade,
      assessedAt: new Date().toISOString(),
    };
  });
  if (!changes.length) return { draft, changes };
  return {
    draft: {
      ...draft,
      entries,
      updatedAt: new Date().toISOString(),
    },
    changes,
  };
}

export function uniqueCatalogContests(
  contests: ContestCatalogEntry[],
): ContestCatalogEntry[] {
  const seen = new Set<string>();
  return contests.filter((contest) => {
    const id = String(contest?.id || '');
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function isSelfEvalRowComplete(
  entry: ContestSelfEvalEntry | null | undefined,
): boolean {
  return String(entry?.value || '').trim() !== '' && Boolean(String(entry?.recordedDate || '').trim());
}

function entryForContest(
  entries: Record<string, ContestSelfEvalEntry> | undefined,
  contestId: string,
): ContestSelfEvalEntry | undefined {
  if (!entries) return undefined;
  if (entries[contestId]) return entries[contestId];
  return Object.values(entries).find((item) => item?.contestId === contestId);
}

export function countSelfEvalSkillsOnDate(
  entries: Record<string, ContestSelfEvalEntry>,
  date: string,
  exceptContestId?: string,
  allowedContestIds?: string[],
): number {
  const day = String(date || '').trim();
  if (!day) return 0;
  const allowed = allowedContestIds ? new Set(allowedContestIds) : null;
  return Object.entries(entries || {}).filter(([id, entry]) => {
    if (exceptContestId && (id === exceptContestId || entry?.contestId === exceptContestId)) return false;
    if (allowed && !allowed.has(id)) return false;
    return String(entry?.recordedDate || '').trim() === day;
  }).length;
}

export function overloadedSelfEvalDates(
  entries: Record<string, ContestSelfEvalEntry>,
  maxPerDate = MAX_SELF_EVAL_SKILLS_PER_DATE,
  allowedContestIds?: string[],
): string[] {
  const counts: Record<string, number> = {};
  const allowed = allowedContestIds ? new Set(allowedContestIds) : null;
  Object.entries(entries || {}).forEach(([id, entry]) => {
    if (allowed && !allowed.has(id)) return;
    const day = String(entry?.recordedDate || '').trim();
    if (!day) return;
    counts[day] = (counts[day] || 0) + 1;
  });
  return Object.keys(counts).filter((day) => counts[day] > maxPerDate);
}

export function contestSelfEvalQuarter(value?: string | Date | null): string {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return contestSelfEvalQuarter(new Date());
  const quarter = Math.floor(date.getMonth() / 3) + 1;
  return `${date.getFullYear()}-Q${quarter}`;
}

export function contestSelfEvalQuarterLabel(value?: string | Date | null): string {
  const key = contestSelfEvalQuarter(value);
  const [year, quarter] = key.split('-');
  return quarter && year ? `${quarter} ${year}` : key;
}

export function isSelfEvalLockedThisQuarter(
  status?: string | null,
  submittedAt?: string | null,
): boolean {
  if (status !== 'submitted') return false;
  if (!submittedAt) return true;
  return contestSelfEvalQuarter(submittedAt) === contestSelfEvalQuarter();
}

export function buildFilledCount(draft: ContestSelfEvalDraft): number {
  return Object.values(draft.entries).filter((e) => String(e.value || '').trim() !== '').length;
}

export function listFilledSelfEvalSkills(
  draft: ContestSelfEvalDraft,
  contests: ContestCatalogEntry[],
): { contestId: string; title: string; index: number }[] {
  return uniqueCatalogContests(contests)
    .map((contest, index) => ({ contest, index: index + 1 }))
    .filter(({ contest }) => isSelfEvalRowComplete(entryForContest(draft.entries, contest.id)))
    .map(({ contest, index }) => ({
      contestId: contest.id,
      title: contest.title,
      index,
    }));
}

export function listIncompleteSelfEvalSkills(
  draft: ContestSelfEvalDraft,
  contests: ContestCatalogEntry[],
): { contestId: string; title: string; index: number; missing: string[] }[] {
  return uniqueCatalogContests(contests)
    .map((contest, index) => {
      const entry = entryForContest(draft.entries, contest.id);
      const missing: string[] = [];
      if (!String(entry?.value || '').trim()) missing.push('score');
      if (!String(entry?.recordedDate || '').trim()) missing.push('date');
      return { contestId: contest.id, title: contest.title, index: index + 1, missing };
    })
    .filter((row) => row.missing.length > 0);
}

export function resolveSelfEvalConsentRole(
  user?: {
    canAddChildren?: boolean;
    children?: unknown[];
    loginMethod?: string;
  } | null,
  actingKind?: 'self' | 'child' | null,
): ContestSelfEvalConsentRole {
  if (actingKind === 'child') return 'parent';
  if (actingKind === 'self') return 'self';
  if (user?.canAddChildren || (Array.isArray(user?.children) && user.children.length > 0)) {
    return 'parent';
  }
  return 'self';
}

export function contestLearnGuidelinesPath(
  sportName: string,
  contestId: string,
  ageCategory: string,
): string {
  const query = new URLSearchParams({
    tab: 'learn',
    contest: contestId,
    age: ageCategory,
  });
  return `/contests/${toSportSlug(sportName)}?${query.toString()}`;
}

const GUIDELINE_SECTION_KEYS: ContestSectionKey[] = [
  'liability_disclaimer',
  'safety_guidelines',
  'injury_prevention',
];

export function getSelfEvalGuidelineSnippets(
  contest: ContestCatalogEntry | null | undefined,
  ageCategory: string,
): { key: ContestSectionKey; label: string; content: string; bullets: string[] }[] {
  if (!contest) return [];
  return GUIDELINE_SECTION_KEYS.map((key) => {
    const resolved = getSectionContentForAge(contest, key, ageCategory);
    if (!resolved) return null;
    const bullets = resolved.bullets || [];
    const fromBlocks = (resolved.blocks || [])
      .map((block) => String(block.body || '').trim())
      .filter(Boolean)
      .join('\n');
    const rawContent = String(resolved.content || fromBlocks || '').trim();
    const content = rawContent
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => {
        if (!line) return false;
        const stripped = line.replace(/^[-•]\s*/, '');
        return !bullets.some((bullet) => bullet === stripped || line.includes(bullet));
      })
      .join('\n');
    if (!content && !bullets.length) return null;
    return {
      key,
      label: resolved.label,
      content,
      bullets,
    };
  }).filter((row): row is { key: ContestSectionKey; label: string; content: string; bullets: string[] } => Boolean(row));
}

/** Self-eval is complete when every Foundation skill has a recorded value and date. */
export function isContestSelfEvalComplete(
  draft: ContestSelfEvalDraft | null | undefined,
  contestIds: string[],
): boolean {
  if (!draft || !contestIds.length) return false;
  return contestIds.every((id) => isSelfEvalRowComplete(entryForContest(draft.entries, id)));
}

export function isSelfEvalRowSaved(entry: ContestSelfEvalEntry | null | undefined): boolean {
  return isSelfEvalRowComplete(entry) && Boolean(String(entry?.savedAt || '').trim());
}

export function areAllSelfEvalRowsSaved(
  draft: ContestSelfEvalDraft | null | undefined,
  contestIds: string[],
): boolean {
  if (!draft || !contestIds.length) return false;
  return contestIds.every((id) => isSelfEvalRowSaved(entryForContest(draft.entries, id)));
}

/** Best available self-eval snapshot for contest gating (saved user copy, else session draft). */
export function loadContestSelfEvalForGate(
  sport: string,
  ageCategory: string,
  ownerId?: string | null,
): ContestSelfEvalDraft {
  if (ownerId) {
    const saved = loadSavedContestSelfEval(ownerId, sport, ageCategory);
    if (saved && buildFilledCount(saved) > 0) return saved;
    const playerDraft = loadContestSelfEvalDraft(sport, ageCategory, ownerId);
    if (buildFilledCount(playerDraft) > 0) return playerDraft;
  }
  return loadContestSelfEvalDraft(sport, ageCategory);
}

const READY_PREFIX = 'encycgames.contestReady.v1';

function readyKey(sport: string, ageCategory: string): string {
  return `${READY_PREFIX}:${sport}:${ageCategory}`;
}

export function isContestReadyConfirmed(sport: string, ageCategory: string): boolean {
  try {
    return sessionStorage.getItem(readyKey(sport, ageCategory)) === '1';
  } catch {
    return false;
  }
}

export function setContestReadyConfirmed(sport: string, ageCategory: string, ready: boolean): void {
  try {
    const key = readyKey(sport, ageCategory);
    if (ready) sessionStorage.setItem(key, '1');
    else sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function getEntryUnitHint(contest: ContestCatalogEntry, ageCategory: string): string {
  return getBenchmarkUnit(contest, ageCategory);
}
